import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Play, Pause, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { money, ItemsEditor } from "./billingCommon";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ConfirmDelete } from "@/components/ConfirmDelete";

const FREQ = ["weekly", "biweekly", "monthly", "quarterly", "yearly"];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const input = "w-full h-10 px-3 rounded-lg border border-input text-sm focus:outline-none focus:ring-2 focus:ring-electric";

const badge = { active: "bg-green-100 text-green-700", paused: "bg-amber-100 text-amber-700", cancelled: "bg-slate-100 text-slate-500" };

export default function Recurring() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const { data } = useQuery({ queryKey: ["recurring"], queryFn: () => api.get("/recurring").then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["recurring"] });
  const plans = data?.plans || [];

  const setStatus = async (p, status) => {
    try { await api.put(`/recurring/${p.id}/status?status=${status}`); refresh(); toast.success(`Plan ${status}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (p) => {
    await api.delete(`/recurring/${p.id}`); refresh(); toast.success("Plan deleted");
  };
  const runDue = async () => {
    setRunning(true);
    try {
      const res = await api.post("/recurring/run");
      toast.success(res.data.generated > 0 ? `Generated ${res.data.generated} invoice(s): ${res.data.invoiceNumbers.join(", ")}` : "No plans are due right now");
      refresh(); qc.invalidateQueries({ queryKey: ["invoices"] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  return (
    <div>
      <PageTitle title="Recurring Billing" subtitle="Set up automatic invoice schedules and generate due invoices" action={
        <div className="flex gap-2">
          <Button variant="outline" onClick={runDue} disabled={running} className="gap-1.5" data-testid="run-due-btn">
            <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} /> Run Due {data?.dueCount ? `(${data.dueCount})` : ""}
          </Button>
          <Button className="bg-electric hover:bg-tech gap-1.5" onClick={() => setOpen(true)} data-testid="new-plan-btn"><Plus className="h-4 w-4" /> New Plan</Button>
        </div>
      } />
      <div className="rounded-xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono">
            <tr><th className="text-left p-3">Customer</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Frequency</th><th className="text-left p-3">Next Run</th><th className="text-left p-3">Generated</th><th className="text-left p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {plans.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">No recurring plans yet.</td></tr> :
              plans.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`plan-row-${p.id}`}>
                  <td className="p-3 font-medium text-navy">{p.customerName}<span className="block text-xs text-slate-400">{p.customerEmail}</span></td>
                  <td className="p-3 text-navy">{money(p.total)}</td>
                  <td className="p-3 capitalize text-slate-600">{p.frequency}</td>
                  <td className="p-3 text-slate-600">{fmtDate(p.nextRunDate)}{p.autoSend && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-electric/10 text-electric align-middle">auto-send</span>}</td>
                  <td className="p-3 text-slate-600">{p.invoicesGenerated}</td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${badge[p.status]}`}>{p.status}</span></td>
                  <td className="p-3"><div className="flex gap-1 justify-end">
                    {p.status === "active" ? (
                      <button title="Pause" onClick={() => setStatus(p, "paused")} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" data-testid={`pause-${p.id}`}><Pause className="h-4 w-4" /></button>
                    ) : p.status === "paused" ? (
                      <button title="Resume" onClick={() => setStatus(p, "active")} className="p-1.5 rounded hover:bg-green-50 text-green-600" data-testid={`resume-${p.id}`}><Play className="h-4 w-4" /></button>
                    ) : null}
                    <ConfirmDelete onConfirm={() => remove(p)} title="Delete this recurring plan?" description={`The recurring plan for ${p.customerName} will be cancelled and removed.`} testid={`delete-plan-${p.id}`} />
                  </div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {open && <PlanDialog onClose={() => setOpen(false)} onSaved={() => { refresh(); setOpen(false); }} />}
    </div>
  );
}

function PlanDialog({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ customerName: "", customerEmail: "", taxRate: 0, discount: 0, frequency: "monthly", startDate: today, notes: "", autoSend: false });
  const [items, setItems] = useState([{ name: "", description: "", quantity: 1, unitPrice: 0 }]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/recurring", { ...form, taxRate: Number(form.taxRate), discount: Number(form.discount), items: items.filter((i) => i.name) });
      toast.success("Recurring plan created"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const total = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>New Recurring Plan</DialogTitle><DialogDescription>Set a customer, line items, and how often invoices should be generated.</DialogDescription></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="plan-form">
        <div className="grid grid-cols-2 gap-3">
          <input className={input} placeholder="Customer name" required value={form.customerName} onChange={(e) => set("customerName", e.target.value)} data-testid="plan-name" />
          <input className={input} type="email" placeholder="Customer email" required value={form.customerEmail} onChange={(e) => set("customerEmail", e.target.value)} data-testid="plan-email" />
        </div>
        <ItemsEditor items={items} setItems={setItems} />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-600">Frequency
            <select className={input} value={form.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="plan-frequency">{FREQ.map((f) => <option key={f} value={f}>{f}</option>)}</select>
          </label>
          <label className="text-sm text-slate-600">Start date
            <input className={input} type="date" value={form.startDate} onChange={(e) => set("startDate", e.target.value)} data-testid="plan-start" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm text-slate-600">Tax %<input className={input} type="number" value={form.taxRate} onChange={(e) => set("taxRate", e.target.value)} /></label>
          <label className="text-sm text-slate-600">Discount $<input className={input} type="number" value={form.discount} onChange={(e) => set("discount", e.target.value)} /></label>
        </div>
        <textarea className={`${input} h-auto py-2`} rows={2} placeholder="Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        <label className="flex items-start gap-2 text-sm text-slate-600 rounded-lg border border-slate-200 p-3">
          <input type="checkbox" className="mt-0.5" checked={form.autoSend} onChange={(e) => set("autoSend", e.target.checked)} data-testid="plan-autosend" />
          <span><span className="font-medium text-navy">Auto-send invoices by email</span><br /><span className="text-xs text-slate-500">When enabled, each generated invoice is emailed to the customer automatically with a pay link (status stays unpaid).</span></span>
        </label>
        <p className="text-sm text-slate-500 text-right">Per-cycle total: <span className="font-semibold text-navy">{money(total)}</span></p>
        <Button type="submit" className="w-full bg-electric hover:bg-tech" data-testid="plan-save">Create Plan</Button>
      </form>
    </DialogContent></Dialog>
  );
}
